import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resetClientFilters, setClientFilters } from "../../../store/filters/clientFiltersSlice";
import BaseRadio from "../../../components/ui/BaseRadio";
import { Checkbox, RadioGroup } from "@mui/material";

import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseAsyncCitySelect from "../../../components/ui/BaseAsyncCitySelect";
import { useQuery } from "@tanstack/react-query";
import BaseRange from "../../../components/ui/BaseRange";
import BaseDrawer from "../../../components/ui/BaseDrawer";
import BaseDatePicker from "../../../components/ui/BaseDatePicker";
import dayjs from "dayjs";
import {
  fetchMaxOffersCount,
  fetchMaxRate,
  fetchWorkOrderCountries,
} from "../../../services/api/workOrder";
import { fetchCategories, fetchSubCategories, fetchVendors } from "../../../services/api/category";
import BaseSwitch from "../../../components/ui/BaseSwitch";
import BaseTextField from "../../../components/ui/BaseTextField";
import { fetchAllClients } from "../../../services/api/client";
import useAuth from "../../../hooks/useAuth";
import { fetchAllProjects } from "../../../services/api/project";
import { fetchWorkedWithEngineers } from "../../../services/api/user";
import { useCountries } from "../../../hooks/useCountries";
import {
  ALL_CANCELLATION_REASONS,
  CanceledByType,
  CLIENT_CANCELLATION_REASONS,
  CLIENT_ONLY_CANCELLATION_REASONS,
  CurrencySymbol,
  TECH_CANCELLATION_REASONS,
  TECH_ONLY_CANCELLATION_REASONS,
  WorkOrderStatusType,
} from "../../../utils/enum";
import { workOrderStatus, workOrderStatusOptions } from "../../../utils/variables";
import { fetchTeams } from "../../../services/api/teams";

const WorkOrderFilters = ({
  open,
  onClose,
  fetchUserWorkOrders,
  currentStatus,
  refetchStatusCounts,
  isWorkOrderLoading,
  setFiltersChanged,
  hasAppliedFilters,
  projectsData = [],
  clientsData = [],
  teamsData = [],
  companyUsers = [],
}) => {
  const dispatch = useDispatch();
  const [filtersApplied, setFiltersApplied] = useState(false);
  const filters = useSelector((state) => state.clientFilters.workOrders);
  const { auth } = useAuth();
  const companyId = auth?.user?.company?.id;

  const [filteredProjects, setFilteredProjects] = useState([]);
  const [clientSearchInput, setClientSearchInput] = useState("");
  const [projectSearchInput, setProjectSearchInput] = useState("");
  const [teamSearchInput, setTeamSearchInput] = useState("");

  const allCurrencies = Object.keys(CurrencySymbol);

  const [localFilters, setLocalFilters] = useState({
    enableRequestCount: false,
    enableRateRange: false,
    enablePaymentType: false,
    status: [],
    minRate: "",
    maxRate: "",
    minRequests: "",
    maxRequests: "",
    startDate: "",
    endDate: "",
    createStartDate: "",
    createEndDate: "",
    paymentType: "",
    categoryId: "",
    vendorId: "",
    subCategoryIds: [],
    clientIds: [],
    teamIds: [],
    clientId: "",
    projectId: "",
    currency: "",
    engineerCurrency: "",
    provider: { value: "", label: "" },
    country: { value: "", label: "" },
    city: { value: "", label: "" },
    createdById: "",
    id: "",
    canceledBy: "",
    cancellationReason: "",
  });

  const paymentType = localFilters.paymentType;

  const { isPending: isWorkedWithLoading, data: workedWithData = [] } = useQuery({
    queryKey: ["workedWithEngineers", companyId],
    queryFn: () => fetchWorkedWithEngineers(companyId, currentStatus[0]),
    enabled: !!companyId,
  });
  const { data: countries = [], isLoading: isCountriesLoading } = useQuery({
    queryKey: ["workOrderCountries"],
    queryFn: fetchWorkOrderCountries,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: vendors = [] } = useQuery({
    queryKey: ["vendors", localFilters.categoryId],
    queryFn: () => fetchVendors(localFilters.categoryId),
    enabled: !!localFilters.categoryId,
  });

  const { data: subCategories = [] } = useQuery({
    queryKey: ["subCategories", localFilters.vendorId],
    queryFn: () => fetchSubCategories(localFilters.vendorId),
    enabled: !!localFilters.vendorId,
  });
  const {
    data: maxRateData,
    isLoading: isMaxRateLoading,
    refetch: refetchMaxRate,
  } = useQuery({
    queryKey: ["maxRate", paymentType],
    queryFn: () => fetchMaxRate(paymentType),
    enabled: !!paymentType,
  });
  const {
    data: maxOffersData,
    isLoading: isMaxOffersLoading,
    refetch: refetchMaxOffers,
  } = useQuery({
    queryKey: ["maxOffersCount"],
    queryFn: fetchMaxOffersCount,
  });
  useEffect(() => {
    if (filters) {
      setLocalFilters((prev) => ({
        ...prev,
        ...filters,
        enableRequestCount: filters.minRequests !== "" || filters.maxRequests !== "",
        enableRateRange: filters.minRate !== "" || filters.maxRate !== "",
        enablePaymentType: filters.paymentType !== "",
      }));
    }
  }, [filters]);

  useEffect(() => {
    if (maxRateData?.maxRate !== undefined) {
      setLocalFilters((prev) => ({
        ...prev,
        minRate: prev.minRate === "" || prev.minRate > maxRateData.maxRate ? 0 : prev.minRate,
        maxRate:
          prev.maxRate === "" || prev.maxRate > maxRateData.maxRate
            ? maxRateData.maxRate
            : prev.maxRate,
      }));
    }
  }, [maxRateData?.maxRate]);

  useEffect(() => {
    if (!paymentType) {
      setLocalFilters((prev) => ({
        ...prev,
        minRate: "",
        maxRate: "",
      }));
    }
  }, [paymentType]);

  useEffect(() => {
    if (filtersApplied) {
      fetchUserWorkOrders();
      onClose();
      setFiltersApplied(false);
    }
  }, [filtersApplied]);

  useEffect(() => {
    if (filters?.appliedFromDrawer === false) {
      setLocalFilters((prev) => ({
        ...prev,
        status: [],
      }));
    }
  }, [localFilters.currency, localFilters.clientId, localFilters.projectId]);

  const applyFilters = async () => {
    const { enableRequestCount, enableRateRange, enablePaymentType, ...rest } = localFilters;

    const cleanedFilters = {
      ...rest,
      ...(enableRequestCount ? {} : { minRequests: "", maxRequests: "" }),
      ...(enableRateRange ? {} : { minRate: "", maxRate: "" }),
      ...(enablePaymentType ? {} : { paymentType: "" }),
    };
    const filtersToApply = { ...cleanedFilters };
    if (!localFilters.status) delete filtersToApply.status;

    Object.entries(filtersToApply).forEach(([key, val]) => {
      dispatch(setClientFilters({ module: "workOrders", filterName: key, data: val }));
    });

    dispatch(
      setClientFilters({ module: "workOrders", filterName: "pagination", data: { page: 0 } }),
    );
    localFilters.status &&
      localFilters.status.length > 0 &&
      dispatch(
        setClientFilters({ module: "workOrders", filterName: "appliedFromDrawer", data: true }),
      );
    setTimeout(() => {
      refetchStatusCounts();
      setFiltersApplied(true);
    }, 0);
  };
  const handleResetFilters = () => {
    dispatch(resetClientFilters({ module: "workOrders", status: currentStatus }));
    setLocalFilters({
      enableRequestCount: false,
      enableRateRange: false,
      enablePaymentType: false,
      minRate: "",
      maxRate: "",
      minRequests: "",
      maxRequests: "",
      startDate: "",
      endDate: "",
      createStartDate: "",
      createEndDate: "",
      paymentType: "",
      categoryId: "",
      vendorId: "",
      subCategoryIds: [],
      clientIds: [],
      teamIds: [],
      clientId: "",
      projectId: "",
      currency: "",
      engineerCurrency: "",
      status: [],
      provider: { value: "", label: "" },
      country: { value: "", label: "" },
      city: { value: "", label: "" },
      createdById: "",
      id: "",
      canceledBy: "",
      cancellationReason: "",
    });

    setFiltersChanged(true);
  };
  const formattedClientData =
    clientsData?.data?.data?.map((client) => ({
      label: client.clientName,
      value: client.id,
    })) || [];

  const formattedProjectData =
    projectsData?.data?.projects?.map((project) => ({
      label: project.title,
      value: project.id,
      clientId: project.client.id,
    })) || [];

  const formattedTeamData =
    teamsData?.data?.data?.map((team) => ({
      label: team.name,
      value: team.id,
    })) || [];

  useEffect(() => {
    let projectsForClient;

    if (localFilters?.clientIds?.length > 0) {
      projectsForClient = formattedProjectData?.filter((p) =>
        localFilters.clientIds.includes(p.clientId),
      );
    } else {
      projectsForClient = formattedProjectData;
    }

    setFilteredProjects((prev) => {
      const same =
        prev?.length === projectsForClient?.length &&
        prev?.every((p, i) => p.id === projectsForClient[i].id);
      return same ? prev : projectsForClient;
    });
  }, [localFilters?.clientIds, formattedProjectData]);
  const handleClientChange = (event) => {
    const value = event.target.value;
    setLocalFilters((prev) => ({
      ...prev,
      clientId: value,
      projectId: "",
    }));
  };
  const handleTeamChange = (event) => {
    const value = event.target.value;
    setLocalFilters((prev) => ({
      ...prev,
      teamId: value,
    }));
  };

  const handleProjectChange = (event) => {
    const value = event.target.value;
    const selectedProject = formattedProjectData.find((proj) => proj.value === value);

    setLocalFilters((prev) => {
      if (!value || !selectedProject) {
        return {
          ...prev,
          projectId: null,
        };
      }

      return {
        ...prev,
        projectId: value,
        clientIds: prev.clientIds.includes(selectedProject.clientId)
          ? prev.clientIds
          : [...prev.clientIds, selectedProject.clientId],
      };
    });
  };
  const handleCountryChange = (option) => {
    setLocalFilters((prev) => ({
      ...prev,
      country: { value: option.target?.value, label: option.target?.label },
      city: { value: "", label: "" },
    }));
  };
  const hasFilters = !!(
    localFilters?.startDate ||
    localFilters?.endDate ||
    localFilters?.createStartDate ||
    localFilters?.createEndDate ||
    localFilters?.categoryId ||
    localFilters?.vendorId ||
    localFilters?.subCategoryIds?.length ||
    localFilters?.teamIds?.length ||
    localFilters?.clientIds?.length ||
    localFilters?.clientId ||
    localFilters?.projectId ||
    localFilters?.currency ||
    localFilters?.engineerCurrency ||
    localFilters?.status ||
    localFilters?.provider ||
    localFilters?.country?.value ||
    localFilters?.createdById ||
    localFilters?.id ||
    localFilters?.canceledBy ||
    localFilters?.cancellationReason ||
    (localFilters?.enablePaymentType && localFilters?.paymentType) ||
    (localFilters?.enableRateRange && (localFilters?.minRate || localFilters?.maxRate)) ||
    (localFilters?.enableRequestCount &&
      (localFilters?.minRequests !== null || localFilters?.maxRequests !== null))
  );

  const canceledByOptions = [
    { value: CanceledByType.CLIENT, label: "Client" },
    { value: CanceledByType.FREELANCER, label: "Tech" },
  ];

  const visibleCancellationReasons = localFilters.canceledBy === CanceledByType.CLIENT
    ? CLIENT_CANCELLATION_REASONS
    : localFilters.canceledBy === CanceledByType.FREELANCER
    ? TECH_CANCELLATION_REASONS
    : ALL_CANCELLATION_REASONS;

  const handleCancellationReasonChange = (e) => {
    const reason = e.target.value;
    let canceledBy = localFilters.canceledBy;
    if (CLIENT_ONLY_CANCELLATION_REASONS.has(reason)) canceledBy = CanceledByType.CLIENT;
    else if (TECH_ONLY_CANCELLATION_REASONS.has(reason)) canceledBy = CanceledByType.FREELANCER;
    setLocalFilters({ ...localFilters, cancellationReason: reason, canceledBy });
  };

  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      onApply={applyFilters}
      title={
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "24px",
            color: "#686868",
            fontWeight: "400",
          }}
        >
          Search Criteria
        </span>
      }
      saveButtonText="Apply Filters"
      isLoading={isWorkOrderLoading}
      enabled={hasFilters}
      handleResetFilters={handleResetFilters}
    >
      <div>
        <div className="mb-4 grid grid-cols-2 gap-4 items-center">
          <div>
            <label className="block text-sm text-[#686868]">Start Date</label>
            <BaseDatePicker
              className="border-none w-full"
              type="date"
              id="startDate"
              name="startDate"
              maxDate={localFilters.endDate ? dayjs(localFilters.endDate) : undefined}
              value={localFilters.startDate ? dayjs(localFilters.startDate) : null}
              onChange={(event) => {
                const selectedDate = dayjs(event).utcOffset(0, true).format("YYYY-MM-DD");
                setLocalFilters({ ...localFilters, startDate: selectedDate });
              }}
              slotProps={{
                textField: { sx: { ...datePickerStyles } },
              }}
            />
          </div>

          <div>
            <label className="block text-sm text-[#686868]">End Date</label>
            <BaseDatePicker
              className="border-none"
              type="date"
              id="endDate"
              name="endDate"
              minDate={localFilters.startDate ? dayjs(localFilters.startDate) : undefined}
              value={localFilters.endDate ? dayjs(localFilters.endDate) : null}
              onChange={(event) => {
                const selectedDate = dayjs(event).utcOffset(0, true).format("YYYY-MM-DD");
                setLocalFilters({ ...localFilters, endDate: selectedDate });
              }}
              slotProps={{
                textField: { sx: { ...datePickerStyles } },
              }}
            />
          </div>
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Clients"
            name="clientIds"
            multiple
            disableCloseOnSelect
            placeholder="Select Clients"
            options={formattedClientData}
            value={localFilters.clientIds}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                clientIds: e.target.value,
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Teams"
            name="teamIds"
            multiple
            disableCloseOnSelect
            placeholder="Select Teams"
            options={formattedTeamData}
            value={localFilters.teamIds}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                teamIds: e.target.value,
              })
            }
            isClearable
          />
        </div>

        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Project"
            name="projectId"
            placeholder="Select Project"
            options={filteredProjects}
            value={localFilters.projectId}
            onChange={handleProjectChange}
            isClearable
            onInputChange={(_, value) => setProjectSearchInput(value)}
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required
            labelShrink={false}
            name="currency"
            label="Client Currency"
            placeholder="Select Currency"
            options={allCurrencies.map((currency) => ({
              label: currency,
              value: currency,
            }))}
            value={localFilters.currency || ""}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                currency: e.target.value,
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Engineer"
            name="provider"
            placeholder="Select Engineer"
            options={
              workedWithData?.map((eng) => ({
                label: eng.name,
                value: eng.id,
              })) || []
            }
            value={localFilters.provider?.value || ""}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                provider: { value: e.target.value, label: e.target.label },
              })
            }
            isLoading={isWorkedWithLoading}
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required
            labelShrink={false}
            name="engineerCurrency"
            label="Engineer Currency"
            placeholder="Select Engineer Currency"
            options={allCurrencies.map((currency) => ({
              label: currency,
              value: currency,
            }))}
            value={localFilters.engineerCurrency || ""}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                engineerCurrency: e.target.value,
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Country"
            name="country"
            placeholder="Select Country"
            options={
              countries?.map((country) => ({
                label: country,
                value: country,
              })) || []
            }
            value={localFilters.country?.value || ""}
            onChange={handleCountryChange}
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAsyncCitySelect
            required={true}
            labelShrink={false}
            label="City"
            placeholder="Enter City"
            country={localFilters.country?.label}
            value={localFilters.city?.value || ""}
            onChange={(city) =>
              setLocalFilters((prev) => ({
                ...prev,
                city: city
                  ? { value: city, label: city }
                  : { value: "", label: "" },
              }))
            }
          />
        </div>

        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Created By"
            name="createdById"
            placeholder="Select Created By"
            options={
              companyUsers?.data?.users?.map((c) => ({
                label: c.firstName + " " + c.lastName,
                value: c.id,
              })) || []
            }
            value={localFilters.createdById}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                createdById: e.target.value,
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Technology Stacks"
            name="categoryId"
            placeholder="Select Technology Stack"
            options={
              categories?.data?.map((c) => ({
                label: c.category,
                value: c.id,
              })) || []
            }
            value={localFilters.categoryId}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                categoryId: e.target.value,
                vendorId: "",
                subCategoryIds: [],
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Category"
            name="vendorId"
            placeholder="Select Category"
            options={vendors?.data?.map((v) => ({ label: v.name, value: v.id })) || []}
            value={localFilters.vendorId}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                vendorId: e.target.value,
                subCategoryIds: [],
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Skills"
            name="subCategoryIds"
            multiple
            placeholder="Select Skills"
            options={
              subCategories?.data?.map((s) => ({
                label: s.subCategory,
                value: s.id,
              })) || []
            }
            value={localFilters.subCategoryIds}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                subCategoryIds: e.target.value,
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseTextField
            optional={false}
            labelShrink={false}
            label="ID"
            name="id"
            placeholder="Select ID"
            value={localFilters.id}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                id: e.target.value,
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required
            labelShrink={false}
            name="canceledBy"
            label="Cancelled By"
            placeholder="Select Cancelled By"
            options={canceledByOptions}
            value={localFilters.canceledBy || ""}
            onChange={(e) =>
              setLocalFilters({
                ...localFilters,
                canceledBy: e.target.value,
                cancellationReason: "",
              })
            }
            isClearable
          />
        </div>
        <div className="mb-5">
          <BaseAutoSelect
            required
            labelShrink={false}
            name="cancellationReason"
            label="Cancellation Reason"
            placeholder="Select Reason"
            options={visibleCancellationReasons}
            value={localFilters.cancellationReason || ""}
            onChange={handleCancellationReasonChange}
            isClearable
          />
        </div>
        <BaseAutoSelect
          required
          labelShrink={false}
          name="status"
          label="Status"
          placeholder="Select WorkOrder Status"
          options={workOrderStatusOptions}
          multiple
          disableCloseOnSelect
          value={localFilters.status}
          onChange={(e) =>
            setLocalFilters({
              ...localFilters,
              status: e.target.value,
            })
          }
          isClearable
        />
        <div className="mb-4 mt-6 grid grid-cols-2 gap-4 items-center">
          <div>
            <label className="block text-sm text-[#686868]">Creation Start Date</label>
            <BaseDatePicker
              className="border-none w-full"
              type="date"
              id="createStartDate"
              name="createStartDate"
              maxDate={localFilters.createEndDate ? dayjs(localFilters.createEndDate) : undefined}
              value={localFilters.createStartDate ? dayjs(localFilters.createStartDate) : null}
              onChange={(event) => {
                const selectedDate = dayjs(event).utcOffset(0, true).format("YYYY-MM-DD");
                setLocalFilters({ ...localFilters, createStartDate: selectedDate });
              }}
              slotProps={{
                textField: { sx: { ...datePickerStyles } },
              }}
            />
          </div>

          <div>
            <label className="block text-sm text-[#686868]">Creation End Date</label>
            <BaseDatePicker
              className="border-none"
              type="date"
              id="createEndDate"
              name="createEndDate"
              minDate={
                localFilters.createStartDate ? dayjs(localFilters.createStartDate) : undefined
              }
              value={localFilters.createEndDate ? dayjs(localFilters.createEndDate) : null}
              onChange={(event) => {
                const selectedDate = dayjs(event).utcOffset(0, true).format("YYYY-MM-DD");
                setLocalFilters({ ...localFilters, createEndDate: selectedDate });
              }}
              slotProps={{
                textField: { sx: { ...datePickerStyles } },
              }}
            />
          </div>
        </div>
      </div>
    </BaseDrawer>
  );
};

const datePickerStyles = {
  "& .MuiOutlinedInput-root": {
    height: "42px",
    borderRadius: "5px",
    backgroundColor: "white",
    boxShadow: "1px 1px 4px 1px #29292921",
    display: "flex",
    alignItems: "center",
    margin: "6px 1.5px",
    border: "none !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
    boxShadow: "none",
  },
  "& .MuiInputBase-input": {
    padding: "4px 10px",
    fontSize: "13px",
    height: "18px",
    lineHeight: "1.4",
  },
  "& .MuiIconButton-root": {
    padding: "4px",
    backgroundColor: "white !important",
    borderLeft: "none !important",
  },
};

export default WorkOrderFilters;
